import React, { useState, useMemo, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Package,
  Users,
  ClipboardList,
  DollarSign,
  FileText,
  Settings,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Building,
  Tag,
  Grid3X3,
  GitBranch,
  ArrowRightLeft,
  ArrowUp,
  ArrowDown,
  Award,
  CreditCard,
  Landmark,
  Wallet,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Receipt,
  ShoppingCart,
  Shield,
  Monitor,
  Briefcase,
  Calculator,
  Hash,
  List,
  Search,
  Send,
  MapPin,
  Link2,
  Bug,
  Bell,
  BookOpen,
  FileSignature,
  ListTree,
  Network,
  PieChart
} from 'lucide-react'
import { MenuItem } from '../../types'
import { usePermissoes } from '../../hooks/usePermissoes'
import { useAuth } from '../../hooks/useAuth'
import { useEmpresa } from '../../hooks/useEmpresa'
import clsx from 'clsx'

const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/'
  },
  {
    id: 'pessoas',
    label: 'Pessoas',
    icon: Users,
    path: '/pessoas'
  },
  {
    id: 'estoque',
    label: 'Estoque',
    icon: Package,
    path: '/estoque',
    submenu: [
      { id: 'estoques', label: 'Estoques', icon: Building, path: '/estoque/estoques' },
      { id: 'transferencias', label: 'Transferências', icon: ArrowRightLeft, path: '/estoque/transferencias' },
      { id: 'ajustes', label: 'Ajustes', icon: Settings, path: '/estoque/ajustes' },
      { id: 'extrato', label: 'Extrato', icon: FileText, path: '/estoque/extrato' },
      { id: 'curva-abc', label: 'Curva ABC', icon: PieChart, path: '/estoque/curva-abc' },
      { id: 'produto-fornecedor', label: 'De-Para Fornecedor', icon: Link2, path: '/estoque/produto-fornecedor' },
      { id: 'produtos', label: 'Produtos', icon: Package, path: '/estoque/produtos' },
      { id: 'grupos', label: 'Grupos', icon: Grid3X3, path: '/estoque/grupos' },
      { id: 'subgrupos', label: 'Subgrupos', icon: GitBranch, path: '/estoque/subgrupos' },
      { id: 'marcas', label: 'Marcas', icon: Award, path: '/estoque/marcas' },
      { id: 'categorias', label: 'Categorias', icon: Tag, path: '/estoque/categorias' }
    ]
  },
  {
    id: 'pdv',
    label: 'PDV - Ponto de Venda',
    icon: ShoppingCart,
    path: '/vendas/pdv'
  },
  {
    id: 'movimento',
    label: 'Movimento',
    icon: ArrowRightLeft,
    path: '/movimento/entrada',
    submenu: [
      { id: 'movimento-entrada', label: 'Entrada', icon: ArrowDown, path: '/movimento/entrada' },
      { id: 'movimento-saida', label: 'Saída', icon: ArrowUp, path: '/movimento/saida' }
    ]
  },
  {
    id: 'servicos',
    label: 'Serviços',
    icon: Briefcase,
    path: '/servicos',
    submenu: [
      { id: 'cadastro-servicos', label: 'Serviços', icon: Briefcase, path: '/servicos' },
      { id: 'emitir-nfse', label: 'Nota Fiscal de Serviço', icon: Send, path: '/servicos/emitir-nfse' },
      { id: 'ordens-servico', label: 'Ordens de Serviço', icon: ClipboardList, path: '/ordens-servico' }
    ]
  },
  {
    id: 'financeiro',
    label: 'Financeiro',
    icon: DollarSign,
    path: '/financeiro',
    submenu: [
      // Operações Diárias (Alta Frequência)
      { id: 'caixa-diario', label: 'Caixa Diário', icon: Wallet, path: '/financeiro/caixa-diario' },
      { id: 'contas-receber', label: 'Recebimentos', icon: TrendingUp, path: '/financeiro/contas-receber' },

      // Gestão de Pagamentos
      { id: 'contas-pagar', label: 'Pagamentos', icon: TrendingDown, path: '/financeiro/contas-pagar' },

      // Análises e Controle
      { id: 'fluxo-caixa', label: 'Fluxo de Caixa', icon: BarChart3, path: '/financeiro/fluxo-caixa' },
      { id: 'dre', label: 'DRE', icon: PieChart, path: '/financeiro/dre' },
      { id: 'formas-pagamento', label: 'Formas de Pagamento', icon: CreditCard, path: '/financeiro/formas-pagamento' },
      { id: 'carteira-cobranca', label: 'Carteira de Cobrança', icon: Receipt, path: '/financeiro/carteira-cobranca' },
      { id: 'bancos', label: 'Bancos', icon: Landmark, path: '/financeiro/bancos' },
      { id: 'plano-contas', label: 'Plano de Contas', icon: ListTree, path: '/financeiro/plano-contas' },
      { id: 'centros-custo', label: 'Centros de Custo', icon: Network, path: '/financeiro/centros-custo' }
    ]
  },
  {
    id: 'contratos',
    label: 'Contratos',
    icon: FileSignature,
    path: '/contratos',
    submenu: [
      { id: 'contratos-lista', label: 'Contratos', icon: FileSignature, path: '/contratos' },
      { id: 'contratos-modelos', label: 'Modelos de Contrato', icon: BookOpen, path: '/contratos/modelos' }
    ]
  },
  {
    id: 'fiscal',
    label: 'Fiscal',
    icon: Calculator,
    path: '/fiscal',
    submenu: [
      { id: 'codificacao-fiscal', label: 'Codificação Fiscal', icon: Hash, path: '/fiscal/codificacao' },
      { id: 'codificacao-fiscal-ncm', label: 'Cod. Fiscal por NCM', icon: Link2, path: '/fiscal/codificacao-ncm' },
      { id: 'fiscal-de-para', label: 'De-Para Fiscal', icon: ArrowRightLeft, path: '/fiscal/de-para' },
      { id: 'series-fiscais', label: 'Séries Fiscais', icon: Hash, path: '/fiscal/series' },
      { id: 'tabela-icms', label: 'Tabela ICMS', icon: MapPin, path: '/fiscal/tabela-icms' }
    ]
  },
  {
    id: 'configuracoes',
    label: 'Configurações',
    icon: Settings,
    path: '/configuracoes',
    submenu: [
      { id: 'perfis-acesso', label: 'Perfis de Acesso', icon: Shield, path: '/configuracoes/perfis-acesso' },
      { id: 'integracao', label: 'Integrações', icon: Settings, path: '/configuracoes/integracoes' },
      { id: 'terminais-caixa', label: 'Terminais de Caixa', icon: Monitor, path: '/configuracoes/terminais-caixa' },
      { id: 'notificacoes-cobranca', label: 'Notificações de Cobrança', icon: Bell, path: '/configuracoes/notificacoes-cobranca' },
      { id: 'empresa', label: 'Empresa', icon: Building, path: '/configuracoes/empresa' },
      { id: 'usuarios', label: 'Usuários', icon: Users, path: '/configuracoes/usuarios' }
    ]
  },
  {
    id: 'suporte',
    label: 'Suporte',
    icon: Bug,
    path: '/ocorrencias'
  }
]

// Formatar CNPJ: 00.000.000/0000-00
const formatCnpj = (cnpj: string): string => {
  const numbers = cnpj.replace(/\D/g, '')
  if (numbers.length !== 14) return cnpj
  return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

export default function ModernSidebar() {
  const location = useLocation()
  const [expandedItems, setExpandedItems] = useState<string[]>(['dashboard'])
  const [expandedSubgroups, setExpandedSubgroups] = useState<string[]>([])
  const { canAccessMenu } = usePermissoes()
  const { empresaId, loading } = useAuth()
  const { fetchEmpresaById } = useEmpresa()
  const [empresaNome, setEmpresaNome] = useState<string>('')
  const [empresaCnpj, setEmpresaCnpj] = useState<string>('')

  // Carregar dados da empresa atual
  useEffect(() => {
    let cancelado = false
    const carregar = async () => {
      if (!empresaId || loading) return
      try {
        const empresa = await fetchEmpresaById(empresaId)
        if (!cancelado && empresa) {
          setEmpresaNome(empresa.nome_fantasia || empresa.razao_social || '')
          setEmpresaCnpj(empresa.cnpj || '')
        }
      } catch (error) {
        console.error('❌ Sidebar: Erro ao carregar empresa:', error)
      }
    }
    carregar()
    return () => { cancelado = true }
  }, [empresaId, loading])

  // Filtrar menus baseado nas permissões do usuário
  const filteredMenuItems = useMemo(() => {
    return menuItems
      .map(item => {
        if (item.submenu && item.submenu.length > 0) {
          if (!canAccessMenu(item.id, null)) return null
          const filteredSubmenu = item.submenu
            .map(sub => {
              if (sub.submenu && sub.submenu.length > 0) {
                const filteredLeaves = sub.submenu.filter(leaf => canAccessMenu(item.id, leaf.id))
                if (filteredLeaves.length === 0) return null
                return { ...sub, submenu: filteredLeaves }
              }
              if (!canAccessMenu(item.id, sub.id)) return null
              return sub
            })
            .filter((sub): sub is MenuItem => sub !== null)
          if (filteredSubmenu.length === 0) return null
          return { ...item, submenu: filteredSubmenu }
        }
        if (!canAccessMenu(item.id)) return null
        return item
      })
      .filter((item): item is MenuItem => item !== null)
  }, [canAccessMenu])

  const toggleSubmenu = (itemId: string) => {
    setExpandedItems(prev => {
      const isClosing = prev.includes(itemId)
      if (isClosing) setExpandedSubgroups([])
      return isClosing ? prev.filter(id => id !== itemId) : [itemId]
    })
  }

  const toggleSubgroup = (id: string) => {
    setExpandedSubgroups(prev =>
      prev.includes(id) ? [] : [id]
    )
  }

  const isActive = (path: string) => {
    if (location.pathname === path) return true
    const subPaths = menuItems.flatMap(m => [
      ...(m.submenu?.map(s => s.path) || []),
      ...(m.submenu?.flatMap(s => s.submenu?.map(l => l.path) || []) || [])
    ])
    if (subPaths.includes(path)) return location.pathname === path
    // Se o pathname atual é uma rota de submenu conhecida, não ativar por prefixo
    if (subPaths.includes(location.pathname)) return false
    return location.pathname.startsWith(path + '/')
  }

  const isSubmenuActive = (submenu: MenuItem[]) => {
    return submenu.some(item =>
      isActive(item.path) || (item.submenu?.some(leaf => isActive(leaf.path)) ?? false)
    )
  }

  const renderMenuItem = (item: MenuItem) => {
    const hasSubmenu = item.submenu && item.submenu.length > 0
    const isExpanded = expandedItems.includes(item.id)
    const isItemActive = isActive(item.path)
    const isSubmenuItemActive = hasSubmenu && isSubmenuActive(item.submenu!)

    return (
      <div key={item.id} className="mb-1">
        {/* Main Menu Item */}
        <div
          className={clsx(
            'sidebar-item group relative flex items-center justify-between rounded-xl transition-all duration-300 cursor-pointer',
            'hover:bg-surface-light hover:shadow-lg',
            'px-4 py-3',
            {
              'is-active': isItemActive || isSubmenuItemActive,
              'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25': isItemActive || isSubmenuItemActive,
              'bg-surface-light border border-primary-500/30': !isItemActive && !isSubmenuItemActive && isExpanded,
              'hover:border-primary-500/20 border border-transparent': !isItemActive && !isSubmenuItemActive && !isExpanded
            }
          )}
          onClick={() => {
            if (hasSubmenu) {
              toggleSubmenu(item.id)
            }
          }}
        >
          <Link
            to={hasSubmenu ? '#' : item.path}
            className="flex items-center flex-1 min-w-0"
            onClick={(e) => hasSubmenu && e.preventDefault()}
          >
            {/* Icon */}
            <div
              className={clsx(
                'flex items-center justify-center transition-all duration-300',
                'w-5 h-5 mr-3',
                'sidebar-item-icon',
                {
                  'text-white': isItemActive || isSubmenuItemActive,
                  'text-text-secondary group-hover:text-primary-accent': !isItemActive && !isSubmenuItemActive
                }
              )}
            >
              <item.icon className="w-full h-full" />
            </div>

            {/* Label */}
            <span
              className={clsx(
                'font-medium text-xs transition-all duration-300 truncate',
                {
                  'text-white': isItemActive || isSubmenuItemActive,
                  'text-text-secondary group-hover:text-text-primary': !isItemActive && !isSubmenuItemActive
                }
              )}
            >
              {item.label}
            </span>
          </Link>

          {/* Expand Arrow */}
          {hasSubmenu && (
            <div
              className={clsx(
                'transition-all duration-300 ml-2',
                'sidebar-item-chevron',
                {
                  'text-white': isItemActive || isSubmenuItemActive,
                  'text-text-muted group-hover:text-text-secondary': !isItemActive && !isSubmenuItemActive
                }
              )}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </div>
          )}
        </div>

        {/* Submenu */}
        {hasSubmenu && (
          <div
            className={clsx(
              'mt-1 overflow-hidden transition-all duration-300',
              isExpanded ? 'max-h-[900px] opacity-100' : 'max-h-0 opacity-0'
            )}
          >
            <div className="mx-1 p-2 bg-surface-light/50 rounded-xl border border-border-primary/30 shadow-lg shadow-black/20 space-y-1">
            {item.submenu!.map((subItem) => {
              if (subItem.submenu && subItem.submenu.length > 0) {
                const isSubgroupExpanded = expandedSubgroups.includes(subItem.id)
                const isSubgroupActive = subItem.submenu.some(leaf => isActive(leaf.path))
                return (
                  <div key={subItem.id}>
                    <div
                      className={clsx(
                        'flex items-center justify-between px-4 py-2.5 rounded-lg cursor-pointer text-xs transition-all duration-200 border border-transparent',
                        'hover:bg-surface-light hover:border-primary-500/20',
                        isSubgroupActive ? 'text-primary-accent' : 'text-text-secondary'
                      )}
                      onClick={() => toggleSubgroup(subItem.id)}
                    >
                      <div className="flex items-center">
                        <div className="w-4 h-4 mr-3">
                          <subItem.icon className="w-full h-full" />
                        </div>
                        <span className="font-medium truncate">{subItem.label}</span>
                      </div>
                      {isSubgroupExpanded ? (
                        <ChevronDown className="w-3 h-3 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-3 h-3 flex-shrink-0" />
                      )}
                    </div>
                    <div
                      className={clsx(
                        'ml-4 space-y-1 overflow-hidden transition-all duration-300',
                        isSubgroupExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
                      )}
                    >
                      {subItem.submenu.map((leaf) => (
                        <Link
                          key={leaf.id}
                          to={leaf.path}
                          className={clsx(
                            'sidebar-subitem flex items-center px-4 py-2 rounded-lg text-xs transition-all duration-200 group border border-transparent',
                            'hover:bg-surface-light hover:border-primary-500/20',
                            {
                              'bg-primary-500/10 border-primary-500/30 text-primary-accent': isActive(leaf.path),
                              'text-text-secondary hover:text-text-primary': !isActive(leaf.path)
                            }
                          )}
                        >
                          <div className="w-4 h-4 mr-3 transition-colors duration-200">
                            <leaf.icon className="w-full h-full" />
                          </div>
                          <span className="font-medium truncate">{leaf.label}</span>
                          {isActive(leaf.path) && (
                            <div className="ml-auto w-2 h-2 bg-primary-400 rounded-full animate-pulse" />
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                )
              }
              return (
                <Link
                  key={subItem.id}
                  to={subItem.path}
                  className={clsx(
                    'sidebar-subitem flex items-center px-4 py-2.5 rounded-lg text-xs transition-all duration-200 group border border-transparent',
                    'hover:bg-surface-light hover:border-primary-500/20',
                    {
                      'bg-primary-500/10 border-primary-500/30 text-primary-accent': isActive(subItem.path),
                      'text-text-secondary hover:text-text-primary': !isActive(subItem.path)
                    }
                  )}
                >
                  <div className="w-4 h-4 mr-3 transition-colors duration-200">
                    <subItem.icon className="w-full h-full" />
                  </div>
                  <span className="font-medium truncate">{subItem.label}</span>
                  {isActive(subItem.path) && (
                    <div className="ml-auto w-2 h-2 bg-primary-400 rounded-full animate-pulse" />
                  )}
                </Link>
              )
            })}
            </div>
          </div>
        )}

      </div>
    )
  }

  return (
    <div
      className="dark sidebar-compact bg-surface border-r border-border-primary flex flex-col h-screen shadow-xl shadow-black/20 w-72"
    >
      {/* Header — altura alinhada com o ModernHeader (h-16) pra linha fluida */}
      <div className="sidebar-logo h-16 px-6 border-b border-border-primary flex items-center justify-center">
        <img
          src="/vision.png"
          alt="Vision"
          className="object-contain h-9 drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]"
        />
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav flex-1 p-4 overflow-y-auto scrollbar-hide">
        <div className="space-y-2">
          {filteredMenuItems.map(renderMenuItem)}
        </div>
      </nav>

      {/* Footer: Empresa Selecionada */}
      {empresaNome && (
        <div className="px-4 py-3 border-t border-border-primary/60">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary-500/10 border border-primary-500/20 flex items-center justify-center flex-shrink-0">
              <Building className="w-4 h-4 text-primary-accent" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-text-primary truncate leading-tight">
                {empresaNome}
              </p>
              {empresaCnpj && (
                <p className="text-[10px] text-text-muted truncate leading-tight mt-0.5">
                  {formatCnpj(empresaCnpj)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}